<template>
  <AppHeaderBack>
    Upgrade Plan
  </AppHeaderBack>
  <div class="min-h-screen bg-gradient-to-br from-primary-lighter via-white to-primary-lighter pt-8 pb-20 px-4">
    <div class="max-w-7xl mx-auto space-y-10">
      <!-- Header Section -->
      <div class="text-center mb-8">
        <h1 class="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
          Choose Your <span class="text-primary">Perfect Plan</span>
        </h1>
        <p class="text-lg text-gray-600 max-w-2xl mx-auto">
          Unlock more video ideas and grow your channel faster. Cancel anytime.
        </p>
      </div>

      <!-- Current Plan Status -->
      <CurrentPlanStatus :current-plan="currentPlan" />

      <!-- Loading State -->
      <div v-if="isLoading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div v-for="n in 3" :key="n" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col items-center">
          <div class="animate-pulse w-full">
            <div class="h-8 w-8 bg-primary-red-light rounded-full mb-4 mx-auto"></div>
            <div class="h-4 bg-primary-red-light rounded w-3/4 mb-2 mx-auto"></div>
            <div class="h-4 bg-primary-red-light rounded w-1/2 mx-auto"></div>
            <div class="h-10 bg-primary-red-light rounded w-full mt-6"></div>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="errorMessage" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 max-w-xl mx-auto">
        <p>Error loading plans: {{ errorMessage }}</p>
        <button @click="refreshProducts" class="mt-2 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600">
          Try Again
        </button>
      </div>

      <!-- Plans Display -->
      <div v-if="!isLoading && !errorMessage && hasProducts">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          <!-- Free Plan Card -->
          <FreePlanCard
            :plan-details="freePlanDetails"
            :features="freePlanFeatures"
            :is-current-plan="isCurrentPlan('free')"
            @downgrade="downgradeToPlan('free')"
          />

          <!-- Premium Plans -->
          <PremiumPlanCard
            v-for="product in sortedProducts"
            :key="product.id"
            :product="product"
            :is-current-plan="isCurrentProductPlan(product.id)"
            :is-most-popular="isPlanMostPopular(product.id)"
            :plan-description="getPaidPlanDescription(product)"
            :is-upgrade="isUpgrade(product.id)"
            :loading="isLoading"
            @select-plan="onSelectPlan"
          />
        </div>
      </div>

      <!-- FAQ Section -->
      <section class="pt-12 pb-12 px-4">
        <div class="max-w-4xl mx-auto">
          <h2 class="text-3xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h2>
          <div class="space-y-4">
            <div 
              v-for="(faq, index) in faqs" 
              :key="index"
              class="bg-white rounded-lg shadow-sm border border-gray-200 transition overflow-hidden"
            >
              <button
                @click="toggleFaq(index)"
                class="w-full px-6 py-4 text-left bg-white hover:bg-primary/5 focus:outline-none focus:bg-primary/5 transition-colors duration-200 rounded-t-lg"
                :class="{ 'bg-primary/5': openFaqs.includes(index) }"
              >
                <div class="flex items-center justify-between">
                  <h3 class="text-base md:text-lg font-semibold text-primary-dark">{{ faq.question }}</h3>
                  <span 
                    class="material-symbols-outlined text-primary transition-transform duration-200"
                    :class="{ 'rotate-180': openFaqs.includes(index) }"
                  >
                    expand_more
                  </span>
                </div>
              </button>
              <div 
                v-show="openFaqs.includes(index)"
                class="px-6 pb-4 text-sm text-gray-700 leading-relaxed bg-primary/5 rounded-b-lg"
              >
                <span v-if="faq.answer.includes('tyteapps@gmail.com')">
                  {{ faq.answer.split('tyteapps@gmail.com')[0] }}
                  <a href="mailto:tyteapps@gmail.com" class="text-primary hover:underline">tyteapps@gmail.com</a>
                  {{ faq.answer.split('tyteapps@gmail.com')[1] }}
                </span>
                <span v-else>{{ faq.answer }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { getCheckoutUrl } from '@/composables/useLemonSqueezyCheckout';
import { usePayments } from '@/composables/usePayments';
import { mapGetters, mapActions, mapState } from 'vuex';
import AppHeaderBack from '@/components/common/AppHeaderBack.vue';
import FreePlanCard from '@/components/plans/FreePlanCard.vue';
import PremiumPlanCard from '@/components/plans/PremiumPlanCard.vue';
import CurrentPlanStatus from '@/components/plans/CurrentPlanStatus.vue';

export default {
  name: 'UserPlanUpgrade',
  components: {
    AppHeaderBack,
    FreePlanCard,
    PremiumPlanCard,
    CurrentPlanStatus,
  },
  data() {
    return {
      recentPayments: [],
      loadingPayments: false,
      openFaqs: [],
      faqs: [
        {
          question: "Can I change plans later?",
          answer: "Yes! You can upgrade or downgrade your plan at any time from your account settings. Changes take effect immediately."
        },
        {
          question: "What payment methods do you accept?",
          answer: "We accept all major credit cards, debit cards, and PayPal through our secure payment processor."
        },
        {
          question: "Can I cancel anytime?",
          answer: "Yes! You can cancel your subscription at any time from your account settings. There are no long-term commitments or cancellation fees."
        },
        {
          question: "How can I quickly contact TrendzTube team?",
          answer: "You can reach us directly at tyteapps@gmail.com for any questions, support requests, or feedback. We aim to respond within 24 hours."
        },
        {
          question: "What happens when I upgrade?",
          answer: "Your new plan limits take effect immediately after payment. Your usage counter resets, and you gain access to all premium features."
        },
        {
          question: "Are there any setup fees?",
          answer: "No setup fees or hidden charges. You only pay the monthly subscription fee for your chosen plan."
        },
        {
          question: "How many video ideas can I generate each month?",
          answer: "Each plan has a monthly generation limit. The Free Plan allows 3 generations per month (18 video ideas total), TrendzTube Starter allows 100, and TrendzTube Pro allows 500."
        },
        {
          question: "Is the data really from YouTube?",
          answer: "Yes! TrendzTube uses YouTube's official API to fetch trending videos, view counts, engagement metrics, and more. All recommendations are based on real, up-to-date YouTube data."
        }
      ]
    };
  },
  computed: {
    ...mapState('products', {
      products: 'products',
      productsLoading: 'loading',
      productsError: 'error'
    }),
    ...mapGetters('products', [
      'hasProducts',
      'sortedProducts',
      'isCacheValid'
    ]),
    ...mapState(['user', 'profile', 'loading', 'globalSettings']),
    
    isLoading() {
      return this.loading || this.productsLoading;
    },
    errorMessage() {
      return this.productsError;
    },
    currentPlan() {
      return this.profile?.plan;
    },
    freePlanDetails() {
      return this.globalSettings?.plans?.free || {};
    },
    freePlanFeatures() {
      return this.freePlanDetails?.features || [];
    }
  },
  methods: {
    ...mapActions('products', ['fetchProducts', 'refreshProducts', 'clearProducts']),
    ...mapActions(['showAlert']),

    isCurrentPlan(planName) {
      return this.currentPlan?.name === planName;
    },

    isCurrentProductPlan(productId) {
      const currentPlanName = this.currentPlan?.name;
      if (!currentPlanName || currentPlanName === 'free') return false;

      if (!this.globalSettings?.plans) return false;

      const planEntries = Object.entries(this.globalSettings.plans);
      
      for (const [planKey, planData] of planEntries) {
        if (planKey === 'free') continue;
        
        if (planData.id && planData.id === productId.toString()) {
          return planData.name === currentPlanName;
        }
      }

      return false;
    },

    isUpgrade(productId) {
      let currentPosition = 1;
      if (this.globalSettings?.plans) {
        const planEntries = Object.entries(this.globalSettings.plans);
        for (const [, planData] of planEntries) {
          if (planData.name === this.currentPlan?.name) {
            currentPosition = planData.position || 1;
            break;
          }
        }
      }

      let targetPosition = 1;
      if (this.globalSettings?.plans) {
        const planEntries = Object.entries(this.globalSettings.plans);
        for (const [, planData] of planEntries) {
          if (planData.id && planData.id === productId.toString()) {
            targetPosition = planData.position || 1;
            break;
          }
        }
      }

      return targetPosition > currentPosition;
    },

    async downgradeToPlan(planName) {
      if (planName === 'free') {
        this.showAlert({
          message: 'To downgrade to the free plan, you need to cancel your current subscription. You\'ll be redirected to manage your subscription.',
          type: 'info'
        });
        this.$router.push({ path: '/subscription-management', query: { action: 'downgrade' } });
      }
    },

    selectPlan(plan) {
      console.log('[UserPlanUpgrade] selectPlan called with:', plan);
      // You can store the selected plan if needed, e.g. this.selectedPlan = plan;
    },

    onSelectPlan(product, variant) {
      this.selectPlan(variant);
      const userEmail = this.profile?.email;
      const userName = [this.profile?.name, this.profile?.surname].filter(Boolean).join(' ');
      const checkoutUrl = getCheckoutUrl(product, {
        email: userEmail,
        name: userName,
        'checkout[custom][user_id]': this.user?.uid
      });
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        this.showAlert({
          message: 'Unable to generate checkout link. Please try again.',
          type: 'error'
        });
      }
    },

    getPaidPlanDescription(product) {
      if (!this.globalSettings?.plans) return '';
      
      const planEntries = Object.entries(this.globalSettings.plans);
      
      for (const [planKey, planData] of planEntries) {
        if (planKey === 'free') continue;
        
        if (planData.id && planData.id === product.id.toString()) {
          return planData.description || '';
        }
      }
      
      return '';
    },

    isPlanMostPopular(productId) {
      if (!this.globalSettings?.plans) return false;
      
      const planEntries = Object.entries(this.globalSettings.plans);
      
      for (const [planKey, planData] of planEntries) {
        if (planKey === 'free') continue;
        
        if (planData.id && planData.id === productId.toString()) {
          return planData.mostPopular === true;
        }
      }
      
      return false;
    },

    async fetchRecentPayments() {
      this.loadingPayments = true;
      try {
        const { getUserPayments } = usePayments();
        const payments = await getUserPayments(this.user?.uid);
        this.recentPayments = payments.slice(0, 3);
      } catch (err) {
        this.recentPayments = [];
      } finally {
        this.loadingPayments = false;
      }
    },

    toggleFaq(index) {
      if (this.openFaqs.includes(index)) {
        this.openFaqs = this.openFaqs.filter(i => i !== index);
      } else {
        this.openFaqs.push(index);
      }
    },
  },

  async mounted() {
    // Fetch products from store (will use cache if valid)
    try {
      await this.fetchProducts();
    } catch (error) {
      console.error('Failed to load products:', error);
      this.showAlert({
        message: 'Failed to load subscription plans. Please try again.',
        type: 'error'
      });
    }

    // Fetch recent payments
    await this.fetchRecentPayments();
  },

  // Clear products cache when component is destroyed if needed
  beforeUnmount() {
    // Optionally clear cache on unmount if you want fresh data next time
    // this.clearProducts();
  }
};
</script>

<style scoped>
.shadow-lg {
  box-shadow: 0 10px 25px -5px rgba(80, 36, 255, 0.1), 0 10px 10px -5px rgba(80, 36, 255, 0.04);
}
.shadow-2xl {
  box-shadow: 0 25px 50px -12px rgba(80, 36, 255, 0.25);
}
.rounded-lg {
  border-radius: 0.75rem;
}
.rounded-2xl {
  border-radius: 1rem;
}
.border-gray-200 {
  --tw-border-opacity: 1;
  border-color: rgb(229 231 235 / var(--tw-border-opacity));
}
.bg-white {
  --tw-bg-opacity: 1;
  background-color: rgb(255 255 255 / var(--tw-bg-opacity));
}
.bg-gray-100 {
  --tw-bg-opacity: 1;
  background-color: rgb(243 244 246 / var(--tw-bg-opacity));
}
.text-primary-red {
  color: #e11d48;
}
.bg-primary-red {
  background-color: #e11d48;
}
.bg-primary-red-dark {
  background-color: #be123c;
}
.bg-primary-red-light {
  background-color: #fee2e2;
}
.hover\:bg-primary-red-dark:hover {
  background-color: #be123c;
}
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
</style>